<template>
  <div class="st-wrap">
    <!-- ────────────────────────────────────────────────────────────────── -->
    <!-- ── Plan / Floor Plan summary (floorplans-style routes) ───────── -->
    <!-- ────────────────────────────────────────────────────────────────── -->
    <template v-if="isFloorPlanRoute">
      <div class="st-card">

        <!-- Toolbar -->
        <div class="st-toolbar">
          <div class="st-toolbar-head">
            <span class="st-accent-bar" aria-hidden="true"></span>
            <div class="st-toolbar-titles">
              <div class="st-eyebrow">Summary</div>
              <div class="st-toolbar-titlerow">
                <h3 class="st-title">Floor plan mix</h3>
                <span class="st-sub" v-if="reportDetailSummary && reportDetailSummary.length">
                  {{ reportDetailSummary.length }} plan{{ reportDetailSummary.length !== 1 ? 's' : '' }}
                </span>
              </div>
            </div>
          </div>
          <button v-if="isUnitMixReportRoute === false"
            type="button" class="st-update-btn"
            @click="$emit('filterSummary', reportDetailSummary)">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21 12a9 9 0 1 1-9-9c2.97 0 5.66 1.43 7.34 3.65" />
              <polyline points="21 3 21 9 15 9" />
            </svg>
            Update
          </button>
        </div>

        <!-- Table -->
        <div class="st-table-wrap">
          <table class="st-table">
            <thead>
              <tr>
                <th v-for="(header, hi) in headerFields" :key="hi"
                  :class="[
                    'st-th',
                    'st-th--' + alignFor(header.key),
                    header.key === 'checkbox' ? 'st-th--cbx' : '',
                    header.key === 'view_unit_mix' ? 'st-th--action' : '',
                    hi === 1 ? 'st-th--first' : ''
                  ]">
                  <span v-if="header.key === 'checkbox'">
                    <input type="checkbox" class="st-cbx st-cbx--inverse"
                      :checked="allSelected" @change="toggleAllSelected"
                      aria-label="Select all rows" />
                  </span>
                  <button v-else-if="header.sortable && header.key !== 'view_unit_mix'" type="button"
                    class="st-sort-btn" @click="sortFloorPlan(header.key)">
                    <span>{{ header.label }}</span>
                    <svg v-if="floorSort.key === header.key" viewBox="0 0 24 24" width="12" height="12"
                      fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"
                      stroke-linejoin="round" class="st-sort-ic st-sort-ic--active" aria-hidden="true">
                      <path v-if="floorSort.dir === 'asc'" d="M18 15l-6-6-6 6" />
                      <path v-else d="M6 9l6 6 6-6" />
                    </svg>
                    <svg v-else viewBox="0 0 24 24" width="12" height="12" fill="none"
                      stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"
                      class="st-sort-ic" aria-hidden="true">
                      <path d="m7 15 5 5 5-5" />
                      <path d="m7 9 5-5 5 5" />
                    </svg>
                  </button>
                  <span v-else class="st-th-label">{{ header.label }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, ri) in floorPlanRows" :key="ri" class="st-row"
                :class="{ 'st-row--sel': row.rowSelected }">
                <td v-for="(header, ci) in headerFields" :key="ci"
                  :class="[
                    'st-td',
                    'st-td--' + alignFor(header.key),
                    header.key === 'checkbox' ? 'st-td--cbx' : '',
                    header.key === 'view_unit_mix' ? 'st-td--action' : '',
                    ci === 1 ? 'st-td--first' : ''
                  ]">
                  <!-- checkbox -->
                  <template v-if="header.key === 'checkbox'">
                    <input type="checkbox" class="st-cbx" v-model="row.rowSelected"
                      :aria-label="'Select ' + (row.mls_plan_name || 'row')" />
                  </template>
                  <!-- plan name pill / floor plan link -->
                  <template v-else-if="header.key === 'mls_plan_name'">
                    <a v-if="row.mls_floor_plan_feature && Object.keys(row.mls_floor_plan_feature || {}).length"
                      href="#" class="st-plan-code st-plan-code--link"
                      @click.prevent="openFloorPlan(row.mls_floor_plan_feature)">
                      {{ row[header.key] }}
                    </a>
                    <span v-else class="st-plan-code">{{ row[header.key] }}</span>
                  </template>
                  <!-- type cell -->
                  <template v-else-if="header.key === 'type'">
                    <span class="st-type">
                      <strong class="st-type-bed">
                        <template v-if="row.type && row.type.mls_beds === 's'">Studio</template>
                        <template v-else-if="row.type && row.type.mls_beds === 'jr'">Jr 1-bed</template>
                        <template v-else>{{ row.type && row.type.mls_beds }}-bed</template>
                      </strong>
                      <span v-if="row.type && row.type.fp_specs && String(row.type.fp_specs).toLowerCase() !== 'none'"
                        class="st-type-specs">{{ row.type.fp_specs }}</span>
                    </span>
                  </template>
                  <!-- baths via formatter -->
                  <template v-else-if="header.key === 'baths'">
                    <span class="st-num">{{ formatBaths(row) }}</span>
                  </template>
                  <!-- sold % with bar -->
                  <template v-else-if="header.key === 'sold_percentage'">
                    <span v-if="row[header.key] !== '-' && row[header.key] != null" class="st-sold">
                      <span class="st-sold-val">{{ row[header.key] }}%</span>
                      <span class="st-sold-bar">
                        <span class="st-sold-fill"
                          :style="{ width: Math.min(100, Number(row[header.key])) + '%' }"></span>
                      </span>
                    </span>
                    <span v-else class="st-td--dim">–</span>
                  </template>
                  <!-- % units -->
                  <template v-else-if="header.key === 'units_percentage'">
                    <span :class="{ 'st-td--dim': row[header.key] === '-' }">
                      {{ row[header.key] === '-' ? '–' : row[header.key] + '%' }}
                    </span>
                  </template>
                  <!-- money columns -->
                  <template v-else-if="['average_net_price', 'average_net_psf', 'net_price', 'net_psf', 'weighted_average_price', 'weighted_average_psf'].includes(header.key)">
                    <span :class="{ 'st-td--money': true, 'st-td--dim': row[header.key] === '-' || row[header.key] == null }">
                      {{ formatMoney(header.key, row[header.key]) }}
                    </span>
                  </template>
                  <!-- view button -->
                  <template v-else-if="header.key === 'view_unit_mix'">
                    <button type="button" class="st-view-btn" @click="getUnitMix(row.mls_plan_name)">
                      View
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <path d="M7 7h10v10" />
                        <path d="M7 17 17 7" />
                      </svg>
                    </button>
                  </template>
                  <!-- generic -->
                  <template v-else>
                    <span :class="{ 'st-td--dim': row[header.key] === '-' || row[header.key] == null }">
                      {{ formatGeneric(row[header.key]) }}
                    </span>
                  </template>
                </td>
              </tr>
              <tr v-if="!floorPlanRows.length">
                <td :colspan="headerFields.length" class="st-empty">No plans available.</td>
              </tr>
            </tbody>
            <tfoot v-if="isTypeRoute">
              <tr class="st-total">
                <td class="st-total-cell st-total-cell--label" colspan="4">Total</td>
                <td class="st-total-cell st-total-cell--right">{{ averageSize }}</td>
                <td v-for="(field, fi) in numericFields" :key="fi" class="st-total-cell st-total-cell--right">
                  <span v-if="field.key !== 'units_percentage'">{{ calculateTotal(field.key) }}</span>
                </td>
                <td class="st-total-cell" colspan="5"></td>
              </tr>
            </tfoot>
          </table>
        </div>

      </div>

      <!-- Floor-plan PDF preview modal -->
      <transition name="st-fade">
        <div v-if="pdfPreview" class="st-modal-scrim" @click.self="pdfPreview = null">
          <div class="st-modal-fp">
            <header class="st-modal-fp-head">
              <span class="st-accent-bar"></span>
              <div>
                <div class="st-eyebrow">Floor plan</div>
                <h3 class="st-title">Plan preview</h3>
              </div>
              <button type="button" class="st-modal-close" aria-label="Close" @click="pdfPreview = null">
                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
                  stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </header>
            <div class="st-modal-fp-body">
              <a :href="pdfPreview.link" target="_blank" rel="noopener" class="st-modal-fp-link">
                <img :src="pdfPreview.preview" class="st-modal-fp-img" alt="Floor plan preview" />
              </a>
            </div>
          </div>
        </div>
      </transition>
    </template>

    <!-- ────────────────────────────────────────────────────────────────── -->
    <!-- ── City / Neighbour comparison table (default branch) ────────── -->
    <!-- ────────────────────────────────────────────────────────────────── -->
    <template v-else>
      <div class="st-card">
        <div class="st-toolbar">
          <div class="st-toolbar-head">
            <span class="st-accent-bar" aria-hidden="true"></span>
            <div class="st-toolbar-titles">
              <div class="st-eyebrow">Comparison</div>
              <div class="st-toolbar-titlerow">
                <h3 class="st-title">City summary</h3>
                <span class="st-sub" v-if="reportDetailSummary">
                  {{ reportDetailSummary.length }} {{ reportDetailSummary.length === 1 ? 'city' : 'cities' }}
                </span>
              </div>
            </div>
          </div>
          <button v-if="projects" type="button" class="st-update-btn st-update-btn--accent" @click="downloadCSV">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export CSV
          </button>
        </div>

        <div class="st-table-wrap st-table-wrap--scroll">
          <table class="st-table st-table--city">
            <thead>
              <tr>
                <th v-for="(header, hi) in headerFields" :key="hi + '-h'"
                  :class="['st-th', 'st-th--' + alignFor(header.key)]"
                  @click="sortBy(header.key)">
                  <button type="button" class="st-sort-btn">
                    <span>{{ header.label }}</span>
                    <svg v-if="sortKey === header.key" viewBox="0 0 24 24" width="12" height="12"
                      fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"
                      stroke-linejoin="round" class="st-sort-ic st-sort-ic--active" aria-hidden="true">
                      <path v-if="sortDirection === 'asc'" d="M18 15l-6-6-6 6" />
                      <path v-else d="M6 9l6 6 6-6" />
                    </svg>
                    <svg v-else viewBox="0 0 24 24" width="12" height="12" fill="none"
                      stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"
                      class="st-sort-ic" aria-hidden="true">
                      <path d="m7 15 5 5 5-5" />
                      <path d="m7 9 5-5 5 5" />
                    </svg>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody v-for="value in sortedMappedData" :key="value.city">
              <!-- Main city row -->
              <tr class="st-row st-row--city" :class="{ 'st-row--expanded': expandedCountries.includes(value.city) }"
                @click="toggleCountry(value.city)">
                <td v-for="(data, temp_index) in value" :key="value.city + '_' + temp_index"
                  :class="[
                    'st-td',
                    'st-td--' + alignFor(temp_index),
                    isHighlightCol(temp_index) ? 'st-td--hl' : '',
                    temp_index === 'weighted_average_size' ? 'st-td--hl-soft' : ''
                  ]">
                  <span v-if="temp_index === 'city'" class="st-city">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
                      stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"
                      class="st-city-chev"
                      :class="{ 'st-city-chev--down': expandedCountries.includes(data) }" aria-hidden="true">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                    <span class="st-city-name">{{ data }}</span>
                  </span>
                  <span v-else-if="['weighted_average_price', 'weighted_average_psf', 'net_psf', 'net_price'].includes(temp_index)"
                    class="st-td--money">
                    ${{ data != null ? Number(data).toLocaleString() : '–' }}
                  </span>
                  <span v-else-if="temp_index === 'sold_percentage'">
                    {{ data !== '-' && data != null ? data + '%' : '–' }}
                  </span>
                  <span v-else>
                    {{ data != null ? Number(data).toLocaleString() : '–' }}
                  </span>
                </td>
              </tr>

              <!-- Neighbour rows -->
              <template v-if="expandedCountries.includes(value.city)
                && reportDetailSummary[getOriginalIndex(value.city)].neighbour">
                <tr v-for="([navKey, neighbourValue]) in Object.entries(reportDetailSummary[getOriginalIndex(value.city)].neighbour)"
                  :key="value.city + '_nav_' + navKey" class="st-row st-row--nav">
                  <td class="st-td st-td--center">
                    <span class="st-nav-label">
                      <span class="st-nav-bullet"></span>
                      {{ navKey }}
                    </span>
                  </td>
                  <td class="st-td st-td--right">{{ neighbourValue.total_projects }}</td>
                  <td class="st-td st-td--right">{{ neighbourValue.total_units }}</td>
                  <template v-if="projects">
                    <td class="st-td st-td--right">{{ neighbourValue.rlsd_units }}</td>
                    <td class="st-td st-td--right">{{ neighbourValue.sold_units }}</td>
                    <td class="st-td st-td--right">
                      {{ neighbourValue.rlsd_units > 0
                        ? Math.round((neighbourValue.sold_units / neighbourValue.rlsd_units) * 100) : 0 }}%
                    </td>
                    <td class="st-td st-td--right">{{ neighbourValue.confirmed_sold }}</td>
                    <td class="st-td st-td--right">{{ neighbourValue.q1_sold }}</td>
                    <td class="st-td st-td--right">{{ neighbourValue.q2_sold }}</td>
                    <td class="st-td st-td--right st-td--hl-soft">
                      {{ neighbourValue.total_projects > 0
                        ? Math.round(neighbourValue.avg_size / neighbourValue.total_projects) : 0 }}
                    </td>
                    <td class="st-td st-td--right st-td--hl st-td--money">
                      ${{ neighbourValue.total_projects > 0
                        ? Math.round(neighbourValue.gross_price / neighbourValue.total_projects).toLocaleString() : 0 }}
                    </td>
                    <td class="st-td st-td--right st-td--hl st-td--money">
                      ${{ isMarketStrata
                        ? Math.round(neighbourValue.gross_price / neighbourValue.avg_size).toLocaleString()
                        : Number(neighbourValue.gross_price / neighbourValue.avg_size).toFixed(2) }}
                    </td>
                    <td class="st-td st-td--right st-td--hl st-td--money">
                      ${{ neighbourValue.total_projects > 0
                        ? Math.round(neighbourValue.net_price / neighbourValue.total_projects).toLocaleString() : 0 }}
                    </td>
                    <td class="st-td st-td--right st-td--hl st-td--money">
                      ${{ isMarketStrata
                        ? Math.round(neighbourValue.net_price / neighbourValue.avg_size).toLocaleString()
                        : Number(neighbourValue.net_price / neighbourValue.avg_size).toFixed(2) }}
                    </td>
                  </template>
                  <template v-else>
                    <td class="st-td st-td--right">{{ neighbourValue.total_rental_units }}</td>
                    <td class="st-td st-td--right">{{ neighbourValue.total_strata_units }}</td>
                  </template>
                </tr>
              </template>
            </tbody>

            <tfoot>
              <tr class="st-total">
                <td class="st-total-cell st-total-cell--label">Total</td>
                <td v-for="(total, total_index) in summaryTotals" :key="total_index"
                  class="st-total-cell st-total-cell--right">
                  <template v-if="total_index !== 'sold_percentage'">
                    <span v-if="['weighted_average_price', 'weighted_average_psf', 'net_psf', 'net_price'].includes(total_index)">
                      ${{ total != null ? Number(total).toLocaleString() : '–' }}
                    </span>
                    <span v-else>{{ total != null ? Number(total).toLocaleString() : '–' }}</span>
                  </template>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from "vue";
import { useRouter } from "vue2-helpers/vue-router";

const router = useRouter();
const props = defineProps({
  reportDetailSummary: null,
  headerFields: null,
  projects: null,
  isMarketStrata: null,
});

const FLOOR_PLAN_ROUTES = ["/type", "/unit-mix", "/sold-quarter", "/unit-mix-report"];

const isFloorPlanRoute = computed(() => FLOOR_PLAN_ROUTES.includes(router.currentRoute.path));
const isUnitMixReportRoute = computed(() => router.currentRoute.path === "/unit-mix-report");
const isTypeRoute = computed(() => router.currentRoute.path === "/type");

const expandedCountries = ref([]);
const toggleCountry = (country) => {
  const index = expandedCountries.value.indexOf(country);
  if (index > -1) expandedCountries.value.splice(index, 1);
  else expandedCountries.value.push(country);
};

const pdfPreview = ref(null);
const openFloorPlan = (fp) => { pdfPreview.value = fp; };

/* ────────── floor-plan branch ────────── */

const floorSort = ref({ key: "avg_size", dir: "asc" });

const sortFloorPlan = (key) => {
  if (floorSort.value.key === key) {
    floorSort.value = { key, dir: floorSort.value.dir === "asc" ? "desc" : "asc" };
  } else {
    floorSort.value = { key, dir: "asc" };
  }
};

const alignFor = (key) => {
  if (!key) return "left";
  if (["mls_plan_name", "type", "city"].includes(key)) return "left";
  if (["baths", "checkbox"].includes(key)) return "center";
  return "right";
};

const floorPlanRows = computed(() => {
  const rows = Array.isArray(props.reportDetailSummary) ? props.reportDetailSummary.slice() : [];
  const { key, dir } = floorSort.value;
  if (!key) return rows;
  const isMissing = v => v == null || v === "-" || v === "";
  rows.sort((a, b) => {
    let av = a[key], bv = b[key];
    if (key === "type") {
      av = a.type && a.type.mls_beds;
      bv = b.type && b.type.mls_beds;
    }
    const aM = isMissing(av), bM = isMissing(bv);
    if (aM && bM) return 0;
    if (aM) return 1;
    if (bM) return -1;
    const an = Number(av), bn = Number(bv);
    if (!isNaN(an) && !isNaN(bn) && av !== "" && bv !== "") {
      return dir === "asc" ? an - bn : bn - an;
    }
    return dir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });
  return rows;
});

const allSelected = computed(() => {
  const rows = floorPlanRows.value;
  return rows.length > 0 && rows.every(r => r.rowSelected);
});

const toggleAllSelected = () => {
  const on = !allSelected.value;
  floorPlanRows.value.forEach(r => { r.rowSelected = on; });
};

const formatBaths = (row) => {
  if (!row) return "–";
  const baths = Number(row.baths), units = Number(row.units);
  if (!units || isNaN(baths)) return "–";
  return Math.round(baths / units);
};

const formatMoney = (key, val) => {
  if (val === "-" || val == null || val === "") return "–";
  const n = Number(val);
  if (isNaN(n)) return "–";
  return "$" + Math.round(n).toLocaleString();
};

const formatGeneric = (val) => {
  if (val === "-" || val == null || val === "") return "–";
  if (typeof val === "number") return val.toLocaleString();
  return val;
};

const numericFields = computed(() =>
  (props.headerFields || []).filter(field =>
    ["units", "confirmed_sold", "total_size", "units_percentage"].includes(field.key)
  )
);

const calculateTotal = (key) => {
  if (!props.reportDetailSummary || !props.reportDetailSummary.length) return 0;
  if (key !== "avg_size") {
    return Math.round(
      props.reportDetailSummary.reduce((sum, item) => sum + (Number(item[key]) || 0), 0)
    );
  }
  return Math.round(
    props.reportDetailSummary.reduce((sum, item) => sum + (Number(item[key]) || 0), 0) /
      props.reportDetailSummary.length
  );
};

const averageSize = computed(() => {
  const data = props.reportDetailSummary || [];
  if (!data.length) return 0;
  const total = calculateTotal("units");
  const collected = data.reduce((acc, item) => acc + (item.avg_size * item.units || 0), 0);
  return total ? Math.round(collected / total) : 0;
});

const getUnitMix = async (data) => {
  const q = {
    uid: router.currentRoute.query.uid,
    plan: data,
    project: router.currentRoute.query.project,
    sid: router.currentRoute.query.sid,
    fid: router.currentRoute.query.fid,
    is_market_strata: props.isMarketStrata,
  };
  const routeData = router.resolve({ path: "/unit-mix-report", query: q });
  window.open(routeData.href, "_blank");
};

/* ────────── city branch ────────── */

const HIGHLIGHT_COLS = ["weighted_average_price", "weighted_average_psf", "net_psf", "net_price"];
const isHighlightCol = (key) => HIGHLIGHT_COLS.includes(key);

const sortKey = ref(null);
const sortDirection = ref("asc");

const sortBy = (key) => {
  if (!key) return;
  if (sortKey.value === key) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  } else {
    sortKey.value = key;
    sortDirection.value = "asc";
  }
};

const mappedData = computed(() => {
  return (props.reportDetailSummary || []).map((row) =>
    (props.headerFields || []).reduce((acc, header) => {
      acc[header.key] = Object.prototype.hasOwnProperty.call(row, header.key)
        ? row[header.key]
        : null;
      return acc;
    }, {})
  );
});

const sortedMappedData = computed(() => {
  if (!sortKey.value) {
    return [...mappedData.value].sort((a, b) => {
      if (a.city && b.city) return a.city.localeCompare(b.city);
      return 0;
    });
  }
  const key = sortKey.value;
  const direction = sortDirection.value;
  return [...mappedData.value].sort((a, b) => {
    let valA = a[key], valB = b[key];
    if (typeof valA === "string" && (valA.startsWith("$") || valA.endsWith("%"))) {
      valA = parseFloat(valA.replace(/[$,%]/g, ""));
    }
    if (typeof valB === "string" && (valB.startsWith("$") || valB.endsWith("%"))) {
      valB = parseFloat(valB.replace(/[$,%]/g, ""));
    }
    if (valA === null || valA === undefined) return direction === "asc" ? 1 : -1;
    if (valB === null || valB === undefined) return direction === "asc" ? -1 : 1;
    if (valA < valB) return direction === "asc" ? -1 : 1;
    if (valA > valB) return direction === "asc" ? 1 : -1;
    return 0;
  });
});

const summaryTotals = computed(() => {
  const data = (props.reportDetailSummary || []).reduce(
    (acc, curr) => {
      acc.total_projects += curr.total_projects || 0;
      acc.total_units += curr.total_units || 0;
      acc.total_rental_units += curr.total_rental_units || 0;
      acc.total_strata_units += curr.total_strata_units || 0;
      acc.total_released_units += curr.total_released_units || 0;
      acc.total_reported_sold += curr.total_reported_sold || 0;
      acc.sold_percentage += curr.sold_percentage || 0;
      acc.avail_units += curr.avail_units || 0;
      acc.total_confirmed_sold += curr.total_confirmed_sold || 0;
      acc.weighted_average_size += curr.weighted_average_size * curr.total_projects || 0;
      acc.weighted_average_price += curr.weighted_average_price * curr.total_projects || 0;
      acc.weighted_average_psf += Number(curr.weighted_average_psf) * curr.total_projects || 0;
      acc.net_price += Math.round(curr.net_price) * curr.total_projects || 0;
      acc.net_psf += Number(curr.net_psf) * curr.total_projects || 0;
      acc.quarter1_sold += curr.quarter1_sold || 0;
      acc.quarter2_sold += curr.quarter2_sold || 0;
      return acc;
    },
    {
      total_projects: 0, total_units: 0, total_rental_units: 0, total_strata_units: 0,
      total_released_units: 0, sold_percentage: 0, total_reported_sold: 0, avail_units: 0,
      total_confirmed_sold: 0, quarter1_sold: 0, quarter2_sold: 0,
      weighted_average_size: 0, weighted_average_price: 0, weighted_average_psf: 0,
      net_price: 0, net_psf: 0,
    }
  );
  const tp = data.total_projects || 1;
  data.total_confirmed_sold = Math.round(data.total_confirmed_sold);
  data.weighted_average_price = Math.round(data.weighted_average_price / tp);
  data.weighted_average_size = Math.round(data.weighted_average_size / tp);
  data.weighted_average_psf = !props.isMarketStrata
    ? Number(data.weighted_average_psf / tp).toFixed(2)
    : Math.round(data.weighted_average_psf / tp);
  data.net_price = Math.round(data.net_price / tp);
  data.net_psf = !props.isMarketStrata
    ? Number(data.net_psf / tp).toFixed(2)
    : Math.round(data.net_psf / tp);

  const sortedData = {};
  (props.headerFields || []).forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(data, field.key)) {
      sortedData[field.key] = data[field.key];
    }
  });
  return sortedData;
});

const getOriginalIndex = (city) =>
  (props.reportDetailSummary || []).findIndex((item) => item.city === city);

/* ────────── CSV export (city branch) ────────── */

const downloadCSV = () => {
  const csvRows = [];
  const labels = props.headerFields.map(header => `"${header.label}"`);
  csvRows.push(labels.join(","));

  const formatValue = (key, val) => {
    if (val === null || val === undefined || val === "-") return '""';
    let output = val;
    if (["weighted_average_price", "weighted_average_psf", "net_psf", "net_price",
         "average_net_price", "average_net_psf"].includes(key)) {
      if (["weighted_average_psf", "net_psf", "average_net_psf"].includes(key) && !props.isMarketStrata) {
        output = `$${Number(val).toFixed(2)}`;
      } else {
        output = `$${Math.round(Number(val)).toLocaleString()}`;
      }
    } else if (["sold_percentage", "units_percentage"].includes(key)) {
      output = `${val}%`;
    } else if (typeof val === "number") {
      output = val.toLocaleString();
    }
    return `"${String(output).replace(/"/g, '""')}"`;
  };

  props.reportDetailSummary.forEach((row) => {
    csvRows.push(props.headerFields.map(h => formatValue(h.key, row[h.key])).join(","));

    if (row.neighbour && Object.keys(row.neighbour).length > 0) {
      Object.entries(row.neighbour).forEach(([navName, navData]) => {
        const navMapping = {
          city: `  -- ${navName}`,
          total_projects: navData.total_projects,
          total_units: navData.total_units,
          total_released_units: navData.rlsd_units,
          total_reported_sold: navData.sold_units,
          sold_percentage: navData.rlsd_units > 0
            ? Math.round((navData.sold_units / navData.rlsd_units) * 100) : 0,
          total_confirmed_sold: navData.confirmed_sold,
          quarter1_sold: navData.q1_sold,
          quarter2_sold: navData.q2_sold,
          weighted_average_size: navData.total_projects > 0
            ? Math.round(navData.avg_size / navData.total_projects) : 0,
          weighted_average_price: navData.total_projects > 0
            ? Math.round(navData.gross_price / navData.total_projects) : 0,
          weighted_average_psf: navData.avg_size > 0
            ? navData.gross_price / navData.avg_size : 0,
          net_price: navData.total_projects > 0
            ? Math.round(navData.net_price / navData.total_projects) : 0,
          net_psf: (navData.total_projects > 0 && navData.avg_size > 0)
            ? (navData.net_price / navData.avg_size) : 0,
        };
        csvRows.push(props.headerFields.map(h => formatValue(h.key, navMapping[h.key])).join(","));
      });
    }
  });

  const totals = summaryTotals.value;
  csvRows.push(props.headerFields.map(h => {
    if (h.key === "city") return '"GRAND TOTAL"';
    return Object.prototype.hasOwnProperty.call(totals, h.key)
      ? formatValue(h.key, totals[h.key]) : '""';
  }).join(","));

  const csvString = csvRows.join("\n");
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `City_Summary_Report_${new Date().toISOString().slice(0, 10)}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
</script>

<style scoped>
/* ============================================================ */
/* ── SummaryTable (st-*) — refined RPD navy + coral ── */
/* ============================================================ */

.st-wrap {
  font-family: 'Helvetica', 'Helvetica Neue', Arial, sans-serif;
  color: #12283F;
}

/* ── Card shell ── */
.st-card {
  background: #FFFFFF;
  border: 1px solid #E9E8E9;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(18, 40, 63, 0.06), 0 2px 4px rgba(18, 40, 63, 0.04);
}

/* ── Toolbar ── */
.st-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 18px;
  background: #FFFFFF;
  border-bottom: 1px solid #E9E8E9;
}

.st-toolbar-head {
  display: flex;
  align-items: center;
  gap: 13px;
  min-width: 0;
  flex: 1;
}

.st-accent-bar {
  width: 4px;
  height: 28px;
  border-radius: 2px;
  background: #FC5868;
  flex: none;
}

.st-toolbar-titles {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.st-eyebrow {
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: #343376;
}

.st-toolbar-titlerow {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-top: 2px;
  flex-wrap: wrap;
}

.st-title {
  margin: 0;
  font-family: Poppins, sans-serif;
  font-weight: 700;
  font-size: 17px;
  letter-spacing: -0.01em;
  color: #12283F;
  line-height: 1.15;
}

.st-sub {
  font-family: Poppins, sans-serif;
  font-size: 11.5px;
  color: #545454;
}

.st-update-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid #C9CCD2;
  background: #FFFFFF;
  color: #12283F;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 11.5px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  white-space: nowrap;
}

.st-update-btn:hover {
  background: #EDF2FE;
  border-color: #12283F;
}

.st-update-btn--accent {
  background: #12283F;
  color: #FFFFFF;
  border-color: #12283F;
}

.st-update-btn--accent:hover {
  background: #1d3a5a;
  border-color: #1d3a5a;
  color: #FFFFFF;
}

/* ── Table wrapper ── */
.st-table-wrap {
  background: #FFFFFF;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.st-table-wrap--scroll {
  max-height: 80vh;
  overflow-y: auto;
}

.st-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
  color: #12283F;
}

/* ── Headers ── */
.st-th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #12283F;
  color: rgba(255, 255, 255, 0.74);
  padding: 0;
  white-space: nowrap;
  font-weight: 600;
}

.st-th--cbx,
.st-td--cbx {
  width: 38px;
  padding-left: 16px !important;
}

.st-th--action,
.st-td--action {
  width: 70px;
}

.st-th--first .st-sort-btn,
.st-th--first .st-th-label {
  padding-left: 16px;
}

.st-sort-btn {
  width: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 10px 10px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: inherit;
  white-space: nowrap;
}

.st-th-label {
  display: inline-block;
  padding: 10px 10px;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: inherit;
}

.st-th--left .st-sort-btn,
.st-th--left .st-th-label { justify-content: flex-start; text-align: left; }
.st-th--right .st-sort-btn,
.st-th--right .st-th-label { justify-content: flex-end; text-align: right; width: 100%; }
.st-th--center .st-sort-btn,
.st-th--center .st-th-label { justify-content: center; text-align: center; width: 100%; }

.st-sort-ic {
  opacity: 0.4;
  transition: opacity 0.12s, color 0.12s;
  flex: none;
}

.st-sort-ic--active {
  opacity: 1;
  color: #FC5868;
}

.st-sort-btn:hover {
  color: #FFFFFF;
}

.st-sort-btn:hover .st-sort-ic {
  opacity: 0.85;
}

/* ── Body cells ── */
.st-td {
  padding: 9px 10px;
  border-bottom: 1px solid #EEF0F4;
  vertical-align: middle;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  background: #FFFFFF;
}

.st-td--first {
  padding-left: 16px;
}

.st-td--left   { text-align: left; }
.st-td--right  { text-align: right; }
.st-td--center { text-align: center; }

.st-td--dim   { color: #8A8A8A; }
.st-td--muted { color: #545454; }

.st-td--money {
  font-weight: 600;
  color: #12283F;
}

.st-row:hover .st-td {
  background: #EDF2FE;
}

.st-row--sel .st-td {
  background: #EDF2FE;
}

.st-row--sel .st-td:first-child {
  box-shadow: inset 3px 0 0 #FC5868;
}

/* ── Plan code pill ── */
.st-plan-code {
  display: inline-block;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 12px;
  color: #12283F;
  background: #EDF2FE;
  border-radius: 5px;
  padding: 3px 8px;
  min-width: 28px;
  text-align: center;
  line-height: 1;
  text-decoration: none;
  transition: background 0.12s, color 0.12s;
}

.st-plan-code--link {
  cursor: pointer;
}

.st-plan-code--link:hover {
  background: #12283F;
  color: #FFFFFF;
}

/* ── Type ── */
.st-type {
  display: inline-flex;
  flex-direction: column;
  gap: 1px;
  line-height: 1.25;
}

.st-type-bed {
  font-family: Poppins, sans-serif;
  font-weight: 600;
  color: #12283F;
}

.st-type-specs {
  font-size: 11.5px;
  color: #545454;
}

/* ── Sold % ── */
.st-sold {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  min-width: 46px;
}

.st-sold-val {
  font-weight: 500;
  color: #12283F;
}

.st-sold-bar {
  width: 100%;
  height: 3px;
  border-radius: 2px;
  background: #EDF2FE;
  overflow: hidden;
}

.st-sold-fill {
  display: block;
  height: 100%;
  background: #12283F;
  border-radius: 2px;
  transition: width 320ms cubic-bezier(0.2, 0, 0, 1);
}

/* ── Checkboxes ── */
.st-cbx {
  width: 15px;
  height: 15px;
  accent-color: #12283F;
  cursor: pointer;
  vertical-align: middle;
  margin: 0;
}

.st-cbx--inverse {
  accent-color: #FC5868;
}

/* ── View button ── */
.st-view-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 6px;
  border: 1px solid #C9CCD2;
  background: #FFFFFF;
  color: #12283F;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 11.5px;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}

.st-view-btn:hover {
  background: #12283F;
  color: #FFFFFF;
  border-color: #12283F;
}

/* ── Total / footer row ── */
.st-total .st-total-cell {
  position: sticky;
  bottom: 0;
  background: #F7F8FB;
  border-top: 1.5px solid #C9CCD2;
  border-bottom: none;
  padding: 11px 10px;
  font-family: Poppins, sans-serif;
  font-weight: 700;
  font-size: 12px;
  color: #12283F;
  z-index: 2;
}

.st-total-cell--label {
  padding-left: 16px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 10.5px;
  color: #343376;
}

.st-total-cell--right {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* ── Empty row ── */
.st-empty {
  text-align: center;
  color: #8A8A8A;
  padding: 32px 16px !important;
  font-family: Poppins, sans-serif;
  font-size: 12.5px;
}

/* ── City branch specifics ── */
.st-row--city {
  cursor: pointer;
  background: #F7F8FB;
}

.st-row--city .st-td {
  background: #F7F8FB;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 12.5px;
  color: #12283F;
  border-bottom: 1px solid #E9E8E9;
}

.st-row--city:hover .st-td {
  background: #EDF2FE;
}

.st-row--expanded .st-td {
  background: #EDF2FE;
  border-bottom-color: #C9CCD2;
}

.st-city {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.st-city-chev {
  transition: transform 200ms cubic-bezier(0.2, 0, 0, 1);
  color: #545454;
}

.st-city-chev--down {
  transform: rotate(90deg);
  color: #FC5868;
}

.st-city-name {
  font-family: Poppins, sans-serif;
  font-weight: 600;
}

.st-row--nav .st-td {
  background: #FFFFFF;
  font-family: 'Helvetica', 'Helvetica Neue', Arial, sans-serif;
  font-size: 12px;
  color: #545454;
}

.st-row--nav:hover .st-td {
  background: #FAFBFD;
}

.st-nav-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding-left: 18px;
  color: #545454;
}

.st-nav-bullet {
  width: 5px;
  height: 5px;
  border-radius: 999px;
  background: #C9CCD2;
}

/* Highlighted price columns */
.st-td--hl {
  background: #EDF2FE;
  color: #12283F;
}

.st-td--hl-soft {
  background: rgba(237, 242, 254, 0.45);
}

.st-row--city.st-td--hl,
.st-row--city .st-td--hl {
  background: #DDE6F8;
}

.st-row--nav .st-td--hl {
  background: #F2F5FC;
}

.st-row--nav .st-td--hl-soft {
  background: #F7F9FE;
}

/* ── Floor-plan PDF modal ── */
.st-modal-scrim {
  position: fixed;
  inset: 0;
  background: rgba(18, 40, 63, 0.55);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow-y: auto;
}

.st-modal-fp {
  background: #FFFFFF;
  border-radius: 14px;
  width: min(540px, 100%);
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 24px 56px rgba(18, 40, 63, 0.20), 0 8px 16px rgba(18, 40, 63, 0.08);
  display: flex;
  flex-direction: column;
}

.st-modal-fp-head {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 14px 16px;
  border-bottom: 1px solid #E9E8E9;
  flex-shrink: 0;
}

.st-modal-fp-head .st-title {
  font-size: 16px;
}

.st-modal-close {
  margin-left: auto;
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  border: 1px solid #E9E8E9;
  background: #FFFFFF;
  color: #545454;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  flex-shrink: 0;
}

.st-modal-close:hover {
  background: #EDF2FE;
  color: #12283F;
}

.st-modal-fp-body {
  padding: 16px;
  overflow: auto;
}

.st-modal-fp-link {
  display: block;
  cursor: zoom-in;
}

.st-modal-fp-img {
  width: 100%;
  height: auto;
  border-radius: 8px;
  border: 1px solid #E9E8E9;
  display: block;
}

/* fade transition */
.st-fade-enter-active,
.st-fade-leave-active {
  transition: opacity 200ms ease;
}
.st-fade-enter,
.st-fade-leave-to {
  opacity: 0;
}

/* ── Responsive ── */
@media (max-width: 575.98px) {
  .st-toolbar {
    flex-wrap: wrap;
    row-gap: 10px;
    padding: 12px 14px;
  }
  .st-title { font-size: 16px; }
  .st-update-btn { width: 100%; justify-content: center; }
}

/* ── Dark mode ── */
html.dark .st-card {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.32);
}

html.dark .st-toolbar {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.08);
}

html.dark .st-title { color: #d6e4f0; }
html.dark .st-sub { color: rgba(214, 228, 240, 0.6); }
html.dark .st-eyebrow { color: #a5a8e8; }

html.dark .st-update-btn {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.14);
  color: #d6e4f0;
}
html.dark .st-update-btn:hover {
  background: #1d3048;
  border-color: rgba(255, 255, 255, 0.28);
}
html.dark .st-update-btn--accent {
  background: #FC5868;
  border-color: #FC5868;
  color: #FFFFFF;
}
html.dark .st-update-btn--accent:hover {
  background: #e54b5b;
  border-color: #e54b5b;
}

html.dark .st-table-wrap { background: #152030; }
html.dark .st-table { color: #d6e4f0; }
html.dark .st-th {
  background: #0E1B2C;
  color: rgba(214, 228, 240, 0.55);
}
html.dark .st-td {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.06);
}
html.dark .st-row:hover .st-td,
html.dark .st-row--sel .st-td {
  background: #1d3048;
}
html.dark .st-plan-code {
  background: rgba(52, 51, 118, 0.32);
  color: #d6e4f0;
}
html.dark .st-plan-code--link:hover {
  background: #343376;
  color: #FFFFFF;
}
html.dark .st-type-bed { color: #d6e4f0; }
html.dark .st-type-specs { color: rgba(214, 228, 240, 0.7); }
html.dark .st-sold-val { color: #d6e4f0; }
html.dark .st-sold-bar { background: rgba(255, 255, 255, 0.08); }
html.dark .st-sold-fill { background: #a5a8e8; }
html.dark .st-view-btn {
  background: #152030;
  color: #d6e4f0;
  border-color: rgba(255, 255, 255, 0.14);
}
html.dark .st-view-btn:hover {
  background: #FC5868;
  color: #FFFFFF;
  border-color: #FC5868;
}
html.dark .st-total .st-total-cell {
  background: #0E1B2C;
  border-top-color: rgba(255, 255, 255, 0.12);
  color: #d6e4f0;
}
html.dark .st-total-cell--label { color: #a5a8e8; }
html.dark .st-empty { color: rgba(214, 228, 240, 0.45); }

html.dark .st-row--city .st-td {
  background: #1a2a40;
  color: #d6e4f0;
  border-color: rgba(255, 255, 255, 0.08);
}
html.dark .st-row--city:hover .st-td,
html.dark .st-row--expanded .st-td {
  background: #243a55;
}
html.dark .st-row--nav .st-td {
  background: #152030;
  color: rgba(214, 228, 240, 0.7);
}
html.dark .st-row--nav:hover .st-td {
  background: #1a2738;
}
html.dark .st-nav-label { color: rgba(214, 228, 240, 0.7); }
html.dark .st-nav-bullet { background: rgba(255, 255, 255, 0.2); }
html.dark .st-city-chev { color: rgba(214, 228, 240, 0.6); }
html.dark .st-city-chev--down { color: #FC5868; }

html.dark .st-td--hl { background: #1d3048; }
html.dark .st-td--hl-soft { background: #17273a; }
html.dark .st-row--city .st-td--hl { background: #24405e; }
html.dark .st-row--nav .st-td--hl { background: #1a2c43; }
html.dark .st-row--nav .st-td--hl-soft { background: #172435; }
html.dark .st-td--money { color: #d6e4f0; }

html.dark .st-modal-scrim { background: rgba(0, 0, 0, 0.65); }
html.dark .st-modal-fp {
  background: #152030;
  border: 1px solid rgba(255, 255, 255, 0.08);
}
html.dark .st-modal-fp-head {
  border-color: rgba(255, 255, 255, 0.08);
}
html.dark .st-modal-close {
  background: #152030;
  color: rgba(214, 228, 240, 0.65);
  border-color: rgba(255, 255, 255, 0.12);
}
html.dark .st-modal-close:hover {
  background: #1d3048;
  color: #d6e4f0;
}
html.dark .st-modal-fp-img {
  border-color: rgba(255, 255, 255, 0.12);
}
</style>
